import type { ReactElement } from 'react'

import type { WorldLevel } from '@/queries/constants'

import type {
  POST_EVENT_TYPES,
  POST_FEEDBACK_RATINGS,
  POST_SCOPES,
  POST_STATUSES,
  PROGRAM_SUBWORLD_SLUGS,
  PROGRAM_UMBRELLAS,
  WORLD_DOMAINS,
  WORLD_HELP_INTENTS,
} from './constants'

export const POST_TYPES = ['user-post', 'new-session', 'new-series', 'announcement'] as const
export type PostType = (typeof POST_TYPES)[number]

export const QUESTION_TYPES = ['problem-help', 'assignment-help', 'topic-review'] as const
export type QuestionType = (typeof QUESTION_TYPES)[number]

export type LiveHelpInfo = {
  questionType: QuestionType | null
  requestZoomHelp: boolean
}

export const CHANNEL_TYPES = ['question', 'discussion', 'announcement'] as const
export type ChannelType = (typeof CHANNEL_TYPES)[number]

export type ChannelVisibility = 'hidden' | 'public'

export type WorldType = 'academic' | 'non-academic' | 'sat-bootcamp' | 'school-partnership'

export type WorldVisibility = 'public' | 'private' | 'hidden' | 'unlisted'

export type PostEventType = (typeof POST_EVENT_TYPES)[number]

export type PostStatus = (typeof POST_STATUSES)[number]

export type PostScope = (typeof POST_SCOPES)[number]

export type WorldDomain = (typeof WORLD_DOMAINS)[number]

export type WorldHelpIntent = (typeof WORLD_HELP_INTENTS)[number]

export type WorldMemberDetails = {
  helpIntents?: WorldHelpIntent[]
  backfilledMinLevel?: { level: WorldLevel; reason: string }
  newLevelJune2024?: WorldLevel
}

export type LiveHelpRequestCancellationReason = 'learner' | 'tutor'

export type PostFeedbackRating = (typeof POST_FEEDBACK_RATINGS)[number]

export type PostFeedbackItem = {
  userId: number
  rating: PostFeedbackRating | null
  note: string | null
  internalNote: string | null
}

export type PostFeedbackItems = PostFeedbackItem[]

export interface PostEventPayloadBase {
  type: PostEventType
}

interface PostEventPayloadLearnerLeft extends PostEventPayloadBase {
  type: 'learner-left-live-help-session'
  duration: number
}

interface PostEventPayloadUsersMentioned extends PostEventPayloadBase {
  type: 'comment'
  mentionedUserIds: number[]
}

export type PostEventPayload = PostEventPayloadLearnerLeft | PostEventPayloadUsersMentioned

export interface PostPayloadBase {
  type: PostType
}

interface PostPayloadNewSession extends PostPayloadBase {
  type: 'new-session'
  countExistingSessionsFromUserInWorld: number
}

interface PostPayloadNewSeries extends PostPayloadBase {
  type: 'new-series'
  countExistingSessionsFromUserInWorld: number
}

export interface PostPayloadUserPost extends PostPayloadBase {
  type: 'user-post'
  questionType?: QuestionType | null
  mentionedUserIds?: number[]
}

export type PostPayload = PostPayloadNewSession | PostPayloadNewSeries | PostPayloadUserPost

export type UnfollowedPostReason = 'manually-unfollowed' | 'user-left-world' | 'user-left-channel'

export type LeftWorldReason = 'user-left-world' | 'admin-removed' | 'level-changed'

export type CertificationOptionConfig =
  | {
      type: 'course-challenge'
      title?: string | ((courseName: string) => string)
      description?: string | ((courseName: string) => string)
    }
  | {
      type: 'units'
      title?: string | ((courseName: string) => string)
      description?: string | ((courseName: string) => string)
    }
  | {
      type: 'ap'
      title?: string | ((topicName: string) => string)
      description?: string | ((topicName: string) => string)
    }

export type SeriesFilterTypes = 'grade' | 'day'

export type WorldConfig = {
  memberHomeType: 'default' | 'programs'
  nonMemberHomeType: 'default' | 'programs'
  seriesFilters?: SeriesFilterTypes[]
  seriesSort?: 'programs'
  certificationConfig: {
    options: CertificationOptionConfig[]
    getCertifiedText?: string
    hideHowItWorks?: boolean
    hidePortfolioAlert?: boolean
  }
  applicationClosedReason?: string
  levelsToSkip: WorldLevel[]
  layout: 'community' | 'programs'
  programDescription?: string
  getNonMemberMessageForPrivateWorld: () => ReactElement
}

export type ProgramSubworldSlug = (typeof PROGRAM_SUBWORLD_SLUGS)[number]
export type ProgramUmbrella = (typeof PROGRAM_UMBRELLAS)[number]
